import { IconButton, styled, Box, Tooltip } from "@mui/material";
import { MoreVert, Replay, CheckCircle, ClearOutlined , Schedule } from "@mui/icons-material";
import BoltIcon from "@mui/icons-material/Bolt";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { FireEventModal } from "../../pages/Tracking";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Can } from "../Can";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ConfirmationModal from "../ConfirmationModal";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
	marginRight: theme.spacing(1),
	flex: "none",
	alignSelf: "center",
	marginLeft: "auto",
	display: "flex",
	alignItems: "center",
	flexWrap: "nowrap",
	"& > *": {
		margin: theme.spacing(0.5),
	},
	[theme.breakpoints.down("sm")]: {
		marginRight: theme.spacing(0.5),
		marginLeft: 0,
		justifyContent: "flex-end",
		width: "100%",
		"& > *": {
			margin: theme.spacing(0.3),
		},
	},
}));

const ActionIconButton = styled(IconButton)(({ theme, color }) => ({
	borderRadius: "50%",
	padding: theme.spacing(1),
	backgroundColor: color === "primary" ? theme.palette.primary.main : "transparent",
	color: color === "primary" ? theme.palette.primary.contrastText : theme.palette.primary.main,
	boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: color === "primary" 
			? theme.palette.primary.dark 
			: theme.palette.mode === "dark" ? theme.palette.action.hover : theme.palette.grey[200],
		boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
	},
	[theme.breakpoints.down("sm")]: {
		padding: theme.spacing(0.7),
	},
}));

const LoadingIconButton = ({ loading, color, onClick, children, title }) => {
	const [isLoading, setIsLoading] = useState(loading);
	
	const handleClick = async (e) => {
		if (loading) return;
		
		setIsLoading(true);
		try {
			await onClick(e);
		} finally {
			setIsLoading(false);
		}
	};
	
	return (
		<Tooltip title={title} arrow placement="top">
			<span>
				<ActionIconButton
					color={color}
					disabled={isLoading}
					onClick={handleClick}
				>
					{children}
				</ActionIconButton>
			</span>
		</Tooltip>
	);
};

const TicketActionButtons = ({ ticket, onTicketAccepted, onToggleScriptsPanel, scriptsPanelOpen }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	// Reset loading if ticket status changes externally (e.g. socket update)
	React.useEffect(() => { setLoading(false); }, [ticket.status]);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [fireEventOpen, setFireEventOpen] = useState(false);
	const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const [resumingAi, setResumingAi] = useState(false);
	const [aiPaused, setAiPaused] = useState(false);

	// CC_HUMAN_TAKEOVER_OFICIAL (2026-09-25, pedido do usuario): "deve ter um botao no
	// atendimento eu ativo a ia pra aquele lead novamente antes dela voltar pelo tempo que ja ta
	// configurado" -- toda resposta manual por este canal pausa a IA automaticamente do lado do
	// Gestor (ver CloudApiMessageChannel); este botao tira essa pausa na hora, sem esperar o
	// prazo configurado vencer sozinho. Cor reflete o estado real (pedido do usuario: "o botao
	// deve ficar vermelho quando ela estiver desativada e verde ativada").
	useEffect(() => {
		let mounted = true;
		if (ticket?.whatsapp?.type === "cloudapi" && ticket?.id) {
			api
				.get(`/tickets/${ticket.id}/ai-status`)
				.then(({ data }) => {
					if (mounted) setAiPaused(!!data?.paused);
				})
				.catch(() => {});
		}
		return () => { mounted = false; };
	}, [ticket?.id, ticket?.whatsapp?.type]);

	// CC_IA_DESATIVAR_MANUAL (2026-09-28): o botao alterna -- ativa -> desativa, pausada -> reativa.
	const handleResumeAi = async () => {
		setResumingAi(true);
		try {
			if (!aiPaused) {
				await api.post(`/tickets/${ticket.id}/pause-ai`);
				setAiPaused(true);
				toast.success("IA desativada pra este contato.");
				return;
			}
			await api.post(`/tickets/${ticket.id}/resume-ai`);
			setAiPaused(false);
			toast.success("IA reativada pra este contato.");
		} catch (err) {
			toastError(err);
		} finally {
			setResumingAi(false);
		}
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null
			});
			if (status === "closed") {
				window.dispatchEvent(new CustomEvent('pressticket:ticketClosed', { detail: { ticketId: ticket.id } }));
			}
			if (status === "waiting") {
				window.dispatchEvent(new CustomEvent('pressticket:ticketClosed', { detail: { ticketId: ticket.id } }));
			}
			setLoading(false);
			if (status === "open") {
				const isGroup = ticket?.contact?.isGroup;
				const targetTab = isGroup ? "groups" : "open";
				localStorage.setItem("pressticket:changeTab", targetTab);
				navigate(`/tickets/${ticket.id}`);
			} else if (status === "pending") {
				navigate("/tickets");
			} else if (status === "waiting") {
				navigate("/tickets");
			} else if (status === "closed") {
				navigate("/tickets");
			}
			
			setLoading(false);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleCloseTicket = () => {
		setConfirmationOpen(true);
	};

	const handleConfirmClose = () => {
		handleUpdateTicketStatus(null, "closed", user?.id);
	};

	const handleOpenAcceptTicketWithouSelectQueue = () => {
		setAcceptTicketWithouSelectQueueOpen(true);
	};

	const handleAcceptTicket = async (e) => {
		if (ticket.queue === null || ticket.queue === undefined) {
			handleOpenAcceptTicketWithouSelectQueue();
		} else {
			await handleUpdateTicketStatus(e, "open", user?.id);
			if (onTicketAccepted) {
				await onTicketAccepted();
			}
		}
	};

	return (
		<ActionButtonsContainer>
			<FireEventModal open={fireEventOpen} onClose={() => setFireEventOpen(false)} contactId={ticket?.contactId} companyEvents={[]} />
			{ticket.status === "closed" && (
				<LoadingIconButton
					title={t("messagesList.header.buttons.reopen")}
					loading={loading}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					<Replay />
				</LoadingIconButton>
			)}
			{ticket.status === "open" && (
				<>
					<LoadingIconButton
						title={t("messagesList.header.buttons.return")}
						loading={loading}
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						<Replay />
					</LoadingIconButton>
					<LoadingIconButton
						title="Colocar em Espera"
						loading={loading}
						onClick={e => handleUpdateTicketStatus(e, "waiting", null)}
					>
						<Schedule />
					</LoadingIconButton>
					<LoadingIconButton
						title={t("messagesList.header.buttons.resolve")}
						loading={loading}
						color="primary"
						onClick={handleCloseTicket}
					>
						<ClearOutlined />
					</LoadingIconButton>
					<ConfirmationModal
						title={t("tickets.confirmationModal.closeTicket.title")}
						open={confirmationOpen}
						onClose={() => setConfirmationOpen(false)}
						onConfirm={handleConfirmClose}
					>
						{t("tickets.confirmationModal.closeTicket.message")}
					</ConfirmationModal>
					<Tooltip title={t("messagesList.header.buttons.options")} arrow placement="top">
						<ActionIconButton
							onClick={handleOpenTicketOptionsMenu}>
							<MoreVert />
						</ActionIconButton>
					</Tooltip>
					<Tooltip title="Disparar Evento de Conversão">
					<ActionIconButton
						size="small"
						onClick={() => setFireEventOpen(true)}
						sx={{
							background: "linear-gradient(135deg, #1877F2 0%, #0d5fcb 100%)",
							color: "#fff",
							boxShadow: "0 2px 8px rgba(24,119,242,0.45)",
							"&:hover": {
								background: "linear-gradient(135deg, #1565C0 0%, #0a4aa0 100%)",
								boxShadow: "0 4px 12px rgba(24,119,242,0.55)",
								transform: "scale(1.08)",
							},
						}}
					>
						<AdsClickIcon fontSize="small" />
					</ActionIconButton>
				</Tooltip>
				{ticket.whatsapp?.type === "cloudapi" && (
					<Tooltip title={aiPaused ? "IA desativada pra este contato — clique pra reativar" : "IA ativa pra este contato — clique pra desativar"}>
						<span>
							<ActionIconButton
								size="small"
								disabled={resumingAi}
								onClick={handleResumeAi}
								sx={aiPaused ? {
									background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
									color: "#fff",
									boxShadow: "0 2px 8px rgba(239,68,68,0.45)",
									"&:hover": {
										background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
										boxShadow: "0 4px 12px rgba(239,68,68,0.55)",
										transform: "scale(1.08)",
									},
								} : {
									background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
									color: "#fff",
									boxShadow: "0 2px 8px rgba(16,185,129,0.45)",
									opacity: 1,
									cursor: "pointer",
									"&:hover": {
										background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
										boxShadow: "0 2px 8px rgba(16,185,129,0.45)",
										transform: "none",
									},
								}}
							>
								<SmartToyIcon fontSize="small" />
							</ActionIconButton>
						</span>
					</Tooltip>
				)}
				<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "waiting" && (
				<>
					<LoadingIconButton
						title="Retomar Atendimento"
						loading={loading}
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						<Replay />
					</LoadingIconButton>
					<LoadingIconButton
						title={t("messagesList.header.buttons.resolve")}
						loading={loading}
						color="primary"
						onClick={handleCloseTicket}
					>
						<ClearOutlined />
					</LoadingIconButton>
					<Tooltip title={t("messagesList.header.buttons.options")} arrow placement="top">
						<ActionIconButton
							onClick={handleOpenTicketOptionsMenu}>
							<MoreVert />
						</ActionIconButton>
					</Tooltip>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			<Can
				role={user.profile}
				perform="drawer-admin-items:view"
				yes={() => (
					<>
						{ticket.status === "pending" && (
							<>
								<LoadingIconButton
									title={t("messagesList.header.buttons.accept")}
									loading={loading}
									color="primary"
									onClick={handleAcceptTicket}
								>
									<CheckCircle />
								</LoadingIconButton>
					<Tooltip title={t("messagesList.header.buttons.options")} arrow placement="top">
						<ActionIconButton
							onClick={handleOpenTicketOptionsMenu}>
							<MoreVert />
						</ActionIconButton>
					</Tooltip>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
							</>
						)}
					</>
				)}
			/>
			<AcceptTicketWithouSelectQueue
				modalOpen={acceptTicketWithouSelectQueueOpen}
				onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
				ticketId={ticket.id}
				onSuccess={onTicketAccepted}
			/>
			{onToggleScriptsPanel && (
				<Tooltip title="Mensagens e Scripts" arrow placement="top">
					<ActionIconButton
						onClick={onToggleScriptsPanel}
						style={{ color: scriptsPanelOpen ? undefined : undefined }}
						color={scriptsPanelOpen ? "primary" : "default"}
					>
						<BoltIcon />
					</ActionIconButton>
				</Tooltip>
			)}
		</ActionButtonsContainer>
	);
};

export default TicketActionButtons;
